import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { 
  AlertTriangle, Heart, Bitcoin, Briefcase, Smartphone, Globe, Mail,
  TrendingUp, CreditCard, Shield, ArrowRight, Search, FileText, ChevronRight,
  CheckCircle, XCircle, Eye, Flag
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const scamCategoryData = {
  'romance-scams': {
    title: 'Romance Scams',
    icon: Heart,
    color: 'pink',
    description: 'Romance scams occur when criminals create fake profiles on dating sites, social media, or apps to build emotional relationships with victims. Once trust is established, they invent emergencies or investment opportunities to extract money.',
    warningCount: 2340,
    avgLoss: '£12,500',
    warningSignsTitle: 'How to Spot a Romance Scam',
    warningSigns: [
      'They profess strong feelings very quickly',
      'Their profile seems too good to be true',
      'They avoid video calls or meeting in person',
      'They claim to be overseas (military, oil rig, business)',
      'They ask for money for emergencies or travel',
      'Payment requests via crypto, wire transfer, or gift cards'
    ],
    tactics: [
      'Creating attractive fake profiles with stolen photos',
      'Building emotional connection over weeks/months',
      'Inventing emergencies (medical, legal, travel)',
      'Promising to meet after money is sent',
      'Introducing fake investment opportunities',
      'Using multiple personas in coordinated schemes'
    ],
    prevention: [
      'Reverse image search profile photos',
      'Insist on video calls early in the relationship',
      'Never send money to someone you haven\'t met',
      'Be suspicious of people who can never meet',
      'Research their story - many excuses are common',
      'Trust your instincts if something feels wrong'
    ],
    platforms: ['Dating apps', 'Facebook', 'Instagram', 'LinkedIn', 'WhatsApp']
  },
  'crypto-investment-scams': {
    title: 'Crypto Investment Scams',
    icon: Bitcoin,
    color: 'orange',
    description: 'Cryptocurrency investment scams promise high returns through fake trading platforms, Ponzi schemes, or fraudulent ICOs. Victims are lured with promises of guaranteed profits and sophisticated-looking platforms that are actually designed to steal funds.',
    warningCount: 4521,
    avgLoss: '£45,000',
    warningSignsTitle: 'How to Spot Crypto Investment Fraud',
    warningSigns: [
      'Promises of guaranteed returns or no risk',
      'Pressure to invest quickly or "miss out"',
      'Unregulated platforms with no FCA registration',
      'Celebrity endorsements (usually fake)',
      'Initial profits that can\'t be withdrawn',
      'Requests for more investment to "unlock" funds'
    ],
    tactics: [
      'Professional-looking fake trading platforms',
      'Showing fake profits in dashboard',
      'Initial small payouts to build trust',
      'Romance scams combined with investment pitches',
      'Fake customer service and testimonials',
      'Impersonating legitimate crypto exchanges'
    ],
    prevention: [
      'Check FCA register for regulated firms',
      'Research the platform thoroughly',
      'Be very skeptical of guaranteed returns',
      'Never invest based on social media ads',
      'Avoid platforms requiring crypto-only deposits',
      'Start with amounts you can afford to lose'
    ],
    platforms: ['Telegram', 'Instagram', 'Facebook', 'Dating apps', 'WhatsApp']
  },
  'job-offer-scams': {
    title: 'Job Offer Scams',
    icon: Briefcase,
    color: 'blue',
    description: 'Job scams target job seekers with fake employment offers, requesting upfront fees for training, equipment, or background checks. Some involve reshipping stolen goods or money laundering disguised as legitimate work.',
    warningCount: 1876,
    avgLoss: '£2,800',
    warningSignsTitle: 'How to Spot a Fake Job Offer',
    warningSigns: [
      'Job offers received without applying',
      'Requests for upfront fees or payments',
      'Vague job descriptions and requirements',
      'Too-good-to-be-true salaries',
      'Interviews conducted only via chat/text',
      'Requests for bank details before starting'
    ],
    tactics: [
      'Impersonating known companies',
      'Fake job postings on legitimate sites',
      'Offering work-from-home with high pay',
      'Sending fake checks for "equipment"',
      'Requesting personal documents early',
      'Creating urgency to accept quickly'
    ],
    prevention: [
      'Research the company independently',
      'Never pay for a job opportunity',
      'Verify recruiter identity with company',
      'Be suspicious of chat-only interviews',
      'Don\'t share bank details until employed',
      'Check company on Companies House'
    ],
    platforms: ['LinkedIn', 'Indeed', 'WhatsApp', 'Email', 'Facebook groups']
  },
  'whatsapp-scams': {
    title: 'WhatsApp Scams',
    icon: Smartphone,
    color: 'green',
    description: 'WhatsApp scams include impersonation of family/friends, fake prize notifications, phishing links, and requests for verification codes. Scammers exploit the platform\'s trusted messaging nature to deceive victims.',
    warningCount: 3245,
    avgLoss: '£3,200',
    warningSignsTitle: 'How to Spot WhatsApp Scams',
    warningSigns: [
      '"Hi Mum/Dad" messages from unknown numbers',
      'Requests for money citing emergencies',
      'Messages about prizes or competitions',
      'Links to suspicious websites',
      'Requests for verification codes',
      'Messages claiming to be from support'
    ],
    tactics: [
      'Impersonating family members needing money',
      'Fake prize/lottery notifications',
      'Phishing for login credentials',
      'Hijacking accounts via verification codes',
      'Fake customer support messages',
      'Investment scheme pitches'
    ],
    prevention: [
      'Call family directly to verify emergencies',
      'Enable two-step verification',
      'Never share verification codes',
      'Don\'t click links from unknown senders',
      'Be skeptical of prize notifications',
      'Verify new numbers claiming to be contacts'
    ],
    platforms: ['WhatsApp', 'SMS', 'Phone calls']
  },
  'fake-company-scams': {
    title: 'Fake Company Scams',
    icon: Globe,
    color: 'purple',
    description: 'Fake company scams involve non-existent businesses, fraudulent online stores, or companies cloning legitimate brands. Victims pay for goods/services never received or for investments in companies that don\'t exist.',
    warningCount: 2987,
    avgLoss: '£8,500',
    warningSignsTitle: 'How to Spot Fake Companies',
    warningSigns: [
      'No verifiable company registration',
      'Recently created websites',
      'Prices significantly below market rate',
      'Limited or no contact information',
      'Fake or stolen company credentials',
      'No physical address or fake addresses'
    ],
    tactics: [
      'Cloning legitimate company websites',
      'Using similar domain names',
      'Fake company registration documents',
      'Stolen customer testimonials',
      'Professional-looking fake storefronts',
      'Creating fake industry certifications'
    ],
    prevention: [
      'Check Companies House registration',
      'Verify website age with WHOIS lookup',
      'Research company reviews independently',
      'Look up the registered address',
      'Be wary of unusually low prices',
      'Pay with credit card for protection'
    ],
    platforms: ['Social media ads', 'Email', 'Fake websites', 'Google ads']
  },
  'phishing-websites': {
    title: 'Phishing Websites',
    icon: Mail,
    color: 'red',
    description: 'Phishing websites impersonate banks, government agencies, and popular services to steal login credentials, personal information, and financial details. They often reach victims through email, SMS, or social media.',
    warningCount: 5632,
    avgLoss: '£6,800',
    warningSignsTitle: 'How to Spot Phishing Sites',
    warningSigns: [
      'Slightly misspelled domain names',
      'Poor grammar and spelling',
      'Urgent requests for information',
      'Generic greetings instead of your name',
      'Links in emails that don\'t match the sender',
      'Requests for passwords or card details'
    ],
    tactics: [
      'Creating lookalike websites',
      'Using similar logos and branding',
      'Urgent "security alert" emails',
      'Fake login pages capturing credentials',
      'SMS phishing (smishing)',
      'Voice phishing (vishing) follow-ups'
    ],
    prevention: [
      'Always type URLs directly',
      'Check for HTTPS and padlock',
      'Hover over links before clicking',
      'Don\'t enter details via email links',
      'Use password managers',
      'Enable multi-factor authentication'
    ],
    platforms: ['Email', 'SMS', 'Social media', 'Search engine ads']
  },
  'investment-fraud': {
    title: 'Investment Fraud',
    icon: TrendingUp,
    color: 'yellow',
    description: 'Investment fraud includes Ponzi schemes, advance fee fraud, and fake trading platforms promising unrealistic returns. Scammers use sophisticated sales tactics and may show fake profits before requesting more funds.',
    warningCount: 3456,
    avgLoss: '£52,000',
    warningSignsTitle: 'How to Spot Investment Fraud',
    warningSigns: [
      'Guaranteed returns with no risk',
      'Pressure to invest immediately',
      'Unregistered investments',
      'Complex or unclear strategies',
      'Difficulty withdrawing funds',
      'Returns too consistent to be real'
    ],
    tactics: [
      'Cold calling with special offers',
      'Using early investor profits as bait',
      'Creating urgency with limited offers',
      'Impersonating legitimate firms',
      'Fake celebrity endorsements',
      'Referral bonuses for recruiting'
    ],
    prevention: [
      'Check FCA register and warning list',
      'Research the investment thoroughly',
      'Get independent financial advice',
      'Be skeptical of cold callers',
      'Don\'t be pressured into decisions',
      'Understand the investment fully'
    ],
    platforms: ['Cold calls', 'Social media', 'Email', 'Seminars', 'Websites']
  },
  'loan-scams': {
    title: 'Loan Scams',
    icon: CreditCard,
    color: 'cyan',
    description: 'Loan scams target people seeking credit with fake lenders demanding upfront fees, or by cloning legitimate lender identities. Victims pay fees for loans they never receive.',
    warningCount: 1654,
    avgLoss: '£1,500',
    warningSignsTitle: 'How to Spot Loan Scams',
    warningSigns: [
      'Upfront fees before loan approval',
      'Guaranteed approval regardless of credit',
      'Unlicensed or unverifiable lenders',
      'Pressure to pay fees quickly',
      'Contact via social media or text',
      'Vague terms and conditions'
    ],
    tactics: [
      'Cloning legitimate lender websites',
      'Targeting people with poor credit',
      'Requesting fees via untraceable methods',
      'Fake loan approval documents',
      'Impersonating bank employees',
      'Creating fake comparison sites'
    ],
    prevention: [
      'Check FCA register for authorized firms',
      'Never pay upfront fees for loans',
      'Verify contact details independently',
      'Be wary of unsolicited loan offers',
      'Use official comparison sites',
      'Research lender reviews'
    ],
    platforms: ['Social media', 'Email', 'Text messages', 'Fake websites']
  }
};

const ScamCategoryPage = () => {
  const { category } = useParams();
  const data = scamCategoryData[category];
  
  if (!data) {
    return (
      <div className="min-h-screen bg-[#0a1628] pt-28 pb-16">
        <div className="container mx-auto px-4 text-center">
          <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-4">Category Not Found</h1>
          <Link to="/scam-database">
            <Button className="bg-[#1e40af] hover:bg-[#1e3a8a] text-white">
              Browse Fraud Database
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const CategoryIcon = data.icon;

  return (
    <div className="min-h-screen bg-[#0a1628] pt-28 pb-16">
      <Helmet>
        <title>{data.title} - Warning Signs & Prevention | ActionFraudOne</title>
        <meta name="description" content={`Learn about ${data.title.toLowerCase()}: warning signs, typical tactics, and how to protect yourself. ${data.warningCount.toLocaleString()} reports in our database.`} />
        <link rel="canonical" href={`https://actionfraudone.com/scams/${category}`} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": `${data.title} - Warning Signs and Prevention`,
            "description": data.description,
            "publisher": {
              "@type": "Organization",
              "name": "ActionFraudOne"
            }
          })}
        </script>
      </Helmet>

      <div className="container mx-auto px-4">
        {/* Breadcrumbs */}
        <nav className="flex items-center gap-2 text-sm text-gray-500 mb-6">
          <Link to="/" className="hover:text-blue-400">Home</Link>
          <ChevronRight className="w-4 h-4" />
          <Link to="/scam-database" className="hover:text-blue-400">Fraud Database</Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-blue-400">{data.title}</span>
        </nav>

        {/* Header */}
        <div className="flex flex-col md:flex-row items-start gap-6 mb-12">
          <div className={`w-20 h-20 bg-${data.color}-500/20 rounded-2xl flex items-center justify-center flex-shrink-0`}>
            <CategoryIcon className={`w-10 h-10 text-${data.color}-400`} />
          </div>
          <div className="flex-1">
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">{data.title}</h1>
            <p className="text-gray-400 text-lg mb-4">{data.description}</p>
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center gap-2 px-4 py-2 bg-red-500/10 border border-red-500/30 rounded-lg">
                <AlertTriangle className="w-4 h-4 text-red-400" />
                <span className="text-white text-sm">{data.warningCount.toLocaleString()} Reports</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                <TrendingUp className="w-4 h-4 text-orange-400" />
                <span className="text-white text-sm">Avg Loss: {data.avgLoss}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Warning Signs */}
            <Card className="bg-[#0d1d32] border-[#1e40af]/20">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <Eye className="w-6 h-6 text-red-400" />
                  <h2 className="text-xl font-bold text-white">{data.warningSignsTitle}</h2>
                </div>
                <div className="space-y-3">
                  {data.warningSigns.map((sign, index) => (
                    <div key={index} className="flex items-start gap-3 p-3 bg-red-500/5 border border-red-500/20 rounded-lg">
                      <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-400">{sign}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Tactics Used */}
            <Card className="bg-[#0d1d32] border-[#1e40af]/20">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <AlertTriangle className="w-6 h-6 text-orange-400" />
                  <h2 className="text-xl font-bold text-white">Common Tactics Used</h2>
                </div>
                <div className="space-y-3">
                  {data.tactics.map((tactic, index) => (
                    <div key={index} className="flex items-start gap-3 p-3 bg-orange-500/5 border border-orange-500/20 rounded-lg">
                      <AlertTriangle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-400">{tactic}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Prevention Tips */}
            <Card className="bg-[#0d1d32] border-[#1e40af]/20">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <Shield className="w-6 h-6 text-green-400" />
                  <h2 className="text-xl font-bold text-white">How to Protect Yourself</h2>
                </div>
                <div className="space-y-3">
                  {data.prevention.map((tip, index) => (
                    <div key={index} className="flex items-start gap-3 p-3 bg-green-500/5 border border-green-500/20 rounded-lg">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-400">{tip}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Common Platforms */}
            <Card className="bg-[#0d1d32] border-[#1e40af]/20">
              <CardContent className="p-6">
                <h2 className="text-xl font-bold text-white mb-4">Where These Scams Occur</h2>
                <div className="flex flex-wrap gap-2">
                  {data.platforms.map((platform, index) => (
                    <span key={index} className="px-4 py-2 bg-[#0a1628] border border-[#1e40af]/30 rounded-full text-gray-400 text-sm">
                      {platform}
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Check CTA */}
            <Card className="bg-gradient-to-b from-[#1e40af]/20 to-transparent border-[#1e40af]/30">
              <CardContent className="p-6 text-center">
                <Search className="w-12 h-12 text-blue-400 mx-auto mb-4" />
                <h3 className="text-lg font-bold text-white mb-2">Suspect a Scam?</h3>
                <p className="text-gray-400 text-sm mb-6">
                  Enter any website, company, or contact to check for fraud indicators.
                </p>
                <Link to="/">
                  <Button className="w-full bg-gradient-to-r from-[#1e40af] to-cyan-500 hover:from-[#1e3a8a] hover:to-cyan-400 text-white font-bold">
                    Check Now <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Report */}
            <Card className="bg-[#0d1d32] border-[#1e40af]/20">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-white mb-3">Report a Scam</h3>
                <p className="text-gray-400 text-sm mb-4">
                  Help protect others by reporting suspicious activity.
                </p>
                <Link to="/report-a-scam">
                  <Button variant="outline" className="w-full border-red-500/50 text-red-400 hover:bg-red-500/10">
                    <Flag className="w-4 h-4 mr-2" />
                    Submit Report
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Investigation Services */}
            <Card className="bg-[#0d1d32] border-[#1e40af]/20">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-white mb-3">Been Affected?</h3>
                <p className="text-gray-400 text-sm mb-4">
                  Our investigators can help document evidence for legal or banking purposes.
                </p>
                <Link to="/investigations">
                  <Button className="w-full bg-[#1e40af] hover:bg-[#1e3a8a] text-white">
                    <FileText className="w-4 h-4 mr-2" />
                    Investigation Services
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Related Categories */}
            <Card className="bg-[#0d1d32] border-[#1e40af]/20">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-white mb-4">Related Scam Types</h3>
                <div className="space-y-2">
                  {Object.entries(scamCategoryData)
                    .filter(([key]) => key !== category)
                    .slice(0, 4)
                    .map(([key, cat]) => (
                      <Link key={key} to={`/scams/${key}`} className="block p-3 bg-[#0a1628] rounded-lg hover:bg-[#1e40af]/10 transition-colors">
                        <span className="text-gray-400 text-sm hover:text-blue-400">{cat.title}</span>
                      </Link>
                    ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScamCategoryPage;
